/* eslint-disable */
(() => {
  if (window.__stickyProductCTASyncInitialized) return;
  window.__stickyProductCTASyncInitialized = true;

  const stickyVisibilityInstances = new WeakMap();
  const stickyPriceInstances = new WeakSet();

  /**
   * Sets up price syncing for one sticky CTA bar.
   * When a swatch changes inside the bar's product-card-with-variants,
   * we look up the matching variant from the embedded JSON and update
   * the price elements with the pre-formatted strings from Liquid.
   */
  const initStickyVariantPriceSync = (stickyCTA) => {
    const card = stickyCTA.querySelector('product-card-with-variants');
    const priceEl = stickyCTA.querySelector('[data-sticky-sale-price]');
    const compareEl = stickyCTA.querySelector('[data-sticky-compare-price]');
    if (!card || !priceEl || !compareEl || stickyPriceInstances.has(card)) return;

    let variants;
    try {
      variants = JSON.parse(card.querySelector('script.variants').textContent).variants;
    } catch (e) {
      return;
    }
    if (!variants?.length) return;
    stickyPriceInstances.add(card);

    // Build { OptionName: selectedValue } from checked swatches inside the card
    const getSelectedOptions = () => {
      const selected = {};
      card.querySelectorAll('product-swatch input[type="radio"]:checked').forEach((input) => {
        const optName = input.closest('.swatch-element')?.getAttribute('data-option');
        if (optName) selected[optName] = input.value;
      });
      return selected;
    };

    // Find the variant whose options object matches every selected option
    const findVariant = (selected) =>
      variants.find((v) =>
        Object.entries(selected).every(([key, val]) => v.options[key] === val)
      ) ?? null;

    const updatePrice = () => {
      const variant = findVariant(getSelectedOptions());
      if (!variant) return;

      const onSale = Boolean(variant.compare_at_price);

      priceEl.textContent = variant.price;
      priceEl.classList.toggle('price--sale-price', onSale);
      priceEl.classList.toggle('price--regular-price', !onSale);

      compareEl.textContent = onSale ? variant.compare_at_price : '';
      compareEl.classList.toggle('hidden', !onSale);
    };

    card.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        requestAnimationFrame(updatePrice);
      }
    });
  };

  const setStickyDisplay = (stickyCTA, shouldShow) => {
    const nextDisplay = shouldShow ? 'block' : 'none';
    if (stickyCTA.style.display !== nextDisplay) {
      stickyCTA.style.display = nextDisplay;
    }
  };

  // The header section wraps both the desktop header and the mobile nav bar and
  // is the element that actually sticks, so its box is the one to measure at any
  // breakpoint. The inner headers are fallbacks, and the hidden one of the two
  // measures zero, hence the visibility check.
  const HEADER_SELECTORS = ['.header-section', 'header.site-header', 'nav.mobile-nav-bar-wrapper'];

  const getStickyHeader = () => {
    for (const selector of HEADER_SELECTORS) {
      const candidate = document.querySelector(selector);
      if (candidate && candidate.offsetHeight > 0) return candidate;
    }
    return null;
  };

  // Document offset of an element, ignoring any sticky paint offset: offsetTop
  // reflects the static layout position, so this stays correct once the header
  // has stuck to the top of the viewport.
  const getDocumentTop = (element) => {
    let top = 0;
    let node = element;
    while (node) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    return top;
  };

  const getTopSentinel = () => {
    let sentinel = document.querySelector('[data-sticky-cta-top-sentinel]');
    if (sentinel) return sentinel;

    sentinel = document.createElement('span');
    sentinel.setAttribute('data-sticky-cta-top-sentinel', '');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:200px;left:0;width:1px;height:1px;pointer-events:none;opacity:0;';
    document.body.appendChild(sentinel);
    return sentinel;
  };

  // Spans the band of scroll where the header has not settled at the top yet,
  // i.e. while the announcement bar above it is still on screen.
  const getSettleSentinel = () => {
    let sentinel = document.querySelector('[data-sticky-cta-settle-sentinel]');
    if (sentinel) return sentinel;

    sentinel = document.createElement('span');
    sentinel.setAttribute('data-sticky-cta-settle-sentinel', '');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;opacity:0;';
    document.body.appendChild(sentinel);
    return sentinel;
  };

  const getLastElement = (selector) => {
    const elements = document.querySelectorAll(selector);
    return elements.length ? elements[elements.length - 1] : null;
  };

  const initStickyVisibility = ({ stickySelector, triggerSelector, alwaysVisible = false }) => {
    if (typeof IntersectionObserver === 'undefined') return;

    const stickyCTA = document.querySelector(stickySelector);
    const trigger = getLastElement(triggerSelector);
    if (!stickyCTA) return;
    if (!trigger && !alwaysVisible) return;

    const existingInstance = stickyVisibilityInstances.get(stickyCTA);
    if (existingInstance) existingInstance.destroy();

    let triggerVisible = true;
    let nearTop = (window.scrollY || window.pageYOffset || 0) < 200;
    let nearFooter = false;
    let visible = false;
    let updateFrame = null;
    let positionFrame = null;
    let headerObserver = null;

    const settleSentinel = getSettleSentinel();

    let header = getStickyHeader();
    // Where the bar sits once the header is stuck: constant, so it never has to
    // be recomputed while scrolling.
    let settledTop = 0;
    // Height of the scroll band in which the header is still moving.
    let settleDistance = 0;
    let settling = false;
    let scrollBound = false;
    let lastTop = null;

    const writeTop = (value) => {
      const next = `${Math.max(value, 0)}px`;
      if (next === lastTop) return;
      lastTop = next;
      stickyCTA.style.top = next;
    };

    // Only measured while the header is in motion; the rest of the time the
    // cached settled value is written instead.
    const positionNow = () => {
      if (!header) return writeTop(0);
      writeTop(settling ? header.getBoundingClientRect().bottom - 2 : settledTop);
    };

    const schedulePosition = () => {
      if (positionFrame) return;
      positionFrame = window.requestAnimationFrame(() => {
        positionFrame = null;
        positionNow();
      });
    };

    // The scroll listener exists only while the bar is on screen AND the header
    // is inside the settling band. Everywhere else scrolling costs nothing.
    const syncScrollBinding = () => {
      const shouldBind = visible && settling;
      if (shouldBind === scrollBound) return;
      scrollBound = shouldBind;
      if (shouldBind) {
        window.addEventListener('scroll', schedulePosition, { passive: true });
      } else {
        window.removeEventListener('scroll', schedulePosition);
        if (positionFrame) {
          window.cancelAnimationFrame(positionFrame);
          positionFrame = null;
        }
      }
    };

    const measureHeader = () => {
      // Re-resolve if the header went away or was hidden, e.g. a breakpoint
      // swap that replaced it with a different element.
      if (!header || header.offsetHeight === 0) {
        const resolved = getStickyHeader();
        if (resolved !== header) {
          header = resolved;
          observeHeader();
        }
      }
      if (!header) {
        settledTop = 0;
        settleDistance = 0;
        return;
      }
      settledTop = Math.max(header.offsetHeight - 2, 0);
      settleDistance = Math.max(getDocumentTop(header), 0);
      settleSentinel.style.height = `${settleDistance + 1}px`;
    };

    const updateVisibility = () => {
      updateFrame = null;
      // With alwaysVisible the bar shows from page load and only hides over the
      // footer, so it never overlaps it.
      const shouldHide = alwaysVisible ? nearFooter : nearTop || triggerVisible || nearFooter;
      const nextVisible = !shouldHide;
      if (nextVisible !== visible) {
        visible = nextVisible;
        setStickyDisplay(stickyCTA, visible);
        syncScrollBinding();
      }
      // A hidden bar needs no position; it is repositioned when it comes back.
      if (visible) positionNow();
    };

    const scheduleVisibilityUpdate = () => {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(updateVisibility);
    };

    const observeHeader = () => {
      if (headerObserver) headerObserver.disconnect();
      if (!header || typeof ResizeObserver === 'undefined') return;
      headerObserver = new ResizeObserver(() => {
        measureHeader();
        scheduleVisibilityUpdate();
      });
      headerObserver.observe(header);
    };

    const settleObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const nextSettling = entry.isIntersecting;
      if (nextSettling === settling) return;
      settling = nextSettling;
      syncScrollBinding();
      scheduleVisibilityUpdate();
    }, { threshold: 0 });

    const visibilityObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === trigger) {
          triggerVisible = entry.isIntersecting;
        } else if (entry.target.hasAttribute('data-sticky-cta-top-sentinel')) {
          nearTop = entry.isIntersecting || entry.boundingClientRect.top > 0;
        } else {
          nearFooter = entry.isIntersecting;
        }
      });

      scheduleVisibilityUpdate();
    }, { threshold: 0 });

    if (trigger) visibilityObserver.observe(trigger);
    if (!alwaysVisible) visibilityObserver.observe(getTopSentinel());

    const footerImage = document.querySelector('.footer-image');
    const footerTarget = footerImage?.children.length ? footerImage : document.querySelector('footer');
    if (footerTarget) {
      visibilityObserver.observe(footerTarget);
    }

    measureHeader();
    settling = (window.scrollY || window.pageYOffset || 0) <= settleDistance;
    observeHeader();
    settleObserver.observe(settleSentinel);
    // Synchronous first pass so an always-visible bar is placed before it paints.
    updateVisibility();

    stickyVisibilityInstances.set(stickyCTA, {
      destroy() {
        visibilityObserver.disconnect();
        settleObserver.disconnect();
        if (headerObserver) headerObserver.disconnect();
        visible = false;
        syncScrollBinding();
        if (updateFrame) window.cancelAnimationFrame(updateFrame);
        if (positionFrame) window.cancelAnimationFrame(positionFrame);
      }
    });
  };

  const initAll = () => {
    document.querySelectorAll('.sticky-product-cta').forEach(initStickyVariantPriceSync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  window.destroyStickyProductCTAVisibility = (element) => stickyVisibilityInstances.get(element)?.destroy();
  window.initStickyProductCTAVisibility = initStickyVisibility;
  window.initStickyProductCTAPriceSync = initStickyVariantPriceSync;
  window.dispatchEvent(new CustomEvent('stickyProductCTA:ready'));
})();
